<template>
  <div class="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 lg:px-6">
    <div class="max-w-4xl mx-auto">
      <!-- Header -->
      <div
        class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 lg:mb-8 space-y-4 sm:space-y-0"
      >
        <div class="flex-1">
          <h1
            class="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2"
          >
            Edit School Details
          </h1>
          <p class="text-sm sm:text-base text-gray-600">
            Update your school information and settings
          </p>
        </div>

        <div class="flex items-center space-x-3">
          <button
            @click="$router.go(-1)"
            class="px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center space-x-2"
          >
            <ArrowLeft class="w-4 h-4" />
            <span>Cancel</span>
          </button>

          <button
            @click="saveChanges"
            :disabled="isLoading"
            class="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save class="w-4 h-4" />
            <span>{{ isLoading ? "Saving..." : "Save Changes" }}</span>
          </button>
        </div>
      </div>

      <!-- Edit Form -->
      <form
        @submit.prevent="saveChanges"
        class="space-y-6 lg:space-y-8"
      >
        <!-- Basic Information -->
        <div
          class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow border p-4 sm:p-6 lg:p-8"
        >
          <div class="flex items-center space-x-3 mb-6">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-xl flex items-center justify-center"
            >
              <Info class="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
            <h3 class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              Basic Information
            </h3>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                School Name *
              </label>
              <input
                v-model="formData.schoolName"
                type="text"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Enter school name"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                School Type *
              </label>
              <select
                v-model="formData.schoolType"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select school type</option>
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="high">High</option>
                <option value="college">College</option>
                <option value="university">University</option>
              </select>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Establishment Year
              </label>
              <input
                v-model="formData.establishmentYear"
                type="number"
                min="1800"
                :max="new Date().getFullYear()"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="e.g., 1995"
              />
            </div>

            <div class="sm:col-span-2">
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Description
              </label>
              <textarea
                v-model="formData.description"
                rows="4"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                placeholder="Brief description about the school..."
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Address Information -->
        <div
          class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow border p-4 sm:p-6 lg:p-8"
        >
          <div class="flex items-center space-x-3 mb-6">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-xl flex items-center justify-center"
            >
              <MapPin class="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <h3 class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              Address Information
            </h3>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
            <div class="sm:col-span-2">
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Street Address
              </label>
              <textarea
                v-model="formData.address"
                rows="3"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                placeholder="Enter complete street address"
              ></textarea>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Village/Area
              </label>
              <input
                v-model="formData.village"
                type="text"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Village or area name"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Post Office
              </label>
              <input
                v-model="formData.postOffice"
                type="text"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Post office name"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                City
              </label>
              <input
                v-model="formData.city"
                type="text"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="City name"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                State
              </label>
              <input
                v-model="formData.state"
                type="text"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="State name"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                PIN Code *
              </label>
              <input
                v-model="formData.pincode"
                type="text"
                required
                pattern="[0-9]{6}"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="6-digit PIN code"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Country *
              </label>
              <select
                v-model="formData.country"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              >
                <option value="">Select country</option>
                <option
                  v-for="country in countries"
                  :key="country.code"
                  :value="country.code"
                >
                  {{ country.name }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Contact Information -->
        <div
          class="bg-white/80 backdrop-blur-xl rounded-2xl sm:rounded-3xl shadow-xl border p-4 sm:p-6 lg:p-8"
        >
          <div class="flex items-center space-x-3 mb-6">
            <div
              class="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-xl flex items-center justify-center"
            >
              <Phone class="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <h3 class="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              Contact Information
            </h3>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-6">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Primary Phone *
              </label>
              <input
                v-model="formData.primaryPhone"
                type="tel"
                required
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Primary contact number"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Secondary Phone
              </label>
              <input
                v-model="formData.secondaryPhone"
                type="tel"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="Secondary contact number"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Email Address
              </label>
              <input
                v-model="formData.email"
                type="email"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="school@example.com"
              />
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">
                Website
              </label>
              <input
                v-model="formData.website"
                type="url"
                class="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                placeholder="https://www.schoolwebsite.com"
              />
            </div>
          </div>
        </div>

        <!-- Form Actions -->
        <div
          class="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-4"
        >
          <button
            type="button"
            @click="$router.go(-1)"
            class="px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-xl hover:bg-gray-200 transition-colors duration-200"
          >
            Cancel
          </button>

          <button
            type="submit"
            :disabled="isLoading"
            class="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <Save class="w-5 h-5" />
            <span>{{ isLoading ? "Saving Changes..." : "Save Changes" }}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, onMounted, computed } from "vue";
  import { useRouter } from "vue-router";
  import { useStore } from "vuex";
  import { ArrowLeft, Save, Info, MapPin, Phone } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";

  const router = useRouter();
  const store = useStore();
  const { showToast } = useGlobalToast();

  // Loading state
  const isLoading = ref(false);

  // Countries list
  const countries = ref([
    { code: "US", name: "United States" },
    { code: "IN", name: "India" },
    { code: "UK", name: "United Kingdom" },
    { code: "CA", name: "Canada" },
    { code: "AU", name: "Australia" },
    { code: "DE", name: "Germany" },
    { code: "FR", name: "France" },
    { code: "JP", name: "Japan" },
    { code: "CN", name: "China" },
    { code: "BR", name: "Brazil" },
  ]);

  // Form data
  const formData = reactive({
    schoolName: "",
    schoolType: "",
    establishmentYear: "",
    description: "",
    address: "",
    village: "",
    postOffice: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
    primaryPhone: "",
    secondaryPhone: "",
    email: "",
    website: "",
  });

  // Get current school data from store
  const schoolData = computed(() => store.state.schools[0] || null);

  // Initialize form with existing data
  onMounted(async () => {
    // Fetch current school data if not available
    if (!schoolData.value) {
      await store.dispatch("fetchMySchool");
    }

    // Populate form with existing data
    if (schoolData.value) {
      Object.keys(formData).forEach((key) => {
        if (schoolData.value[key] !== undefined) {
          formData[key] = schoolData.value[key] || "";
        }
      });
    }
  });

  // Save changes
  const saveChanges = async () => {
    try {
      isLoading.value = true;

      const updateData = { ...formData };

      if (updateData.establishmentYear) {
        updateData.establishmentYear = parseInt(updateData.establishmentYear);
      }

      await store.dispatch("editSchool", updateData);

      // ✅ Show success toast
      showToast("success", "School details updated successfully!");

      router.go(-1);
    } catch (error) {
      console.error("Error updating school details:", error);

      // ❌ Error toast
      showToast("error", "Failed to update school details.");
    } finally {
      isLoading.value = false;
    }
  };
</script>
